import React from "react";
import { ClockCircleOutlined, LaptopOutlined, MobileOutlined, TabletOutlined } from "@ant-design/icons";
import { Alert, Skeleton, Table } from "antd";
import dayjs from "dayjs";
import orderBy from "lodash.orderby";
import uniqBy from "lodash.uniqby";
import { useTranslation } from "react-i18next";

import { type UserDevice } from "@fitness-app/data-models/entities/UserStatus";

import { useTraineeProfile, useTraineeUserStatus } from "~/modules/Trainee/TraineeProfile/scope";

const typeIcon: Record<string, typeof MobileOutlined> = {
  mobile: MobileOutlined,
  tablet: TabletOutlined,
  desktop: LaptopOutlined,
};

const TraineeDevices = () => {
  const { t } = useTranslation(["trainees", "survey"]);

  const profileQuery = useTraineeProfile();
  const userId = profileQuery.data?.userId;
  const userStatusQuery = useTraineeUserStatus();
  const userStatus = userStatusQuery.data ?? null;

  // A missing row only means no device has reported yet — it says nothing about whether the
  // client signed in, and it must not be confused with a fetch that is still running or failed.
  if (!userStatus && userStatusQuery.isError) {
    return (
      <div style={{ padding: 20 }}>
        <Alert
          message={t("devices.fetchErrorTitle")}
          description={t("devices.fetchErrorDescription")}
          showIcon
          type="error"
        />
      </div>
    );
  }

  // Without a `userId` nothing is fetched at all, so there is no request to wait for.
  if (!userStatus && (profileQuery.isLoading || (userId && !userStatusQuery.isSuccess))) {
    return (
      <div style={{ padding: 20 }}>
        <Skeleton active paragraph={{ rows: 3 }} />
      </div>
    );
  }

  if (!userStatus) {
    return (
      <div style={{ padding: 20 }}>
        <Alert message={t("devices.alertTitle")} description={t("devices.alertDescription")} showIcon type="warning" />
      </div>
    );
  }

  return (
    <div className="px-2 py-6">
      <Table dataSource={uniqBy(orderBy(userStatus.devices, "lastSeen", "desc"), "name") || []} pagination={false}>
        <Table.Column<UserDevice> dataIndex="name" key="name" title={t("devices.name")} />
        <Table.Column<UserDevice>
          dataIndex="type"
          key="type"
          title={t("devices.type")}
          render={(type: string) => {
            const DeviceIcon = typeIcon[type] || LaptopOutlined;
            return (
              <div className="flex gap-x-2">
                <DeviceIcon /> <span>{t(`devices.types.${type}`)}</span>
              </div>
            );
          }}
        />
        <Table.Column<UserDevice> dataIndex="version" key="version" title={t("devices.appVersion")} />
        <Table.Column<UserDevice>
          key="ota"
          title="OTA"
          render={(_, record: UserDevice) =>
            record.otaUpdateId
              ? `${record.otaUpdateId.slice(0, 8)}${record.otaRuntimeVersion ? ` · ${record.otaRuntimeVersion}` : ""}`
              : "—"
          }
        />
        <Table.Column<UserDevice>
          dataIndex="os"
          key="os"
          title={t("devices.system")}
          render={(os: string, record: UserDevice) => [record.systemName, os].filter(Boolean).join(" ") || "-"}
        />
        <Table.Column<UserDevice>
          dataIndex="lastSeen"
          key="lastSeen"
          title={t("devices.lastSeen")}
          render={(timestamp: number) => (
            <div className="flex gap-x-2">
              <ClockCircleOutlined /> <span>{dayjs(timestamp).format("DD.MM.YYYY HH:mm")}</span>
            </div>
          )}
        />
      </Table>
    </div>
  );
};

export default TraineeDevices;
